// Extracted from original/src/App.tsx:24207-24460 (the password step of the login screen, ADR-08 §2.1).
// Cut: the account-picker grid and "add account" card (:24237-24305, D-038), handleLogin / loginForm state,
// the WhatsApp recovery link that embedded the password (:24398-24420, D-037 / D-133), the add-user modal
// (:24462-...). Added as props-driven states of the same card: username input (D-038) inside the
// "مرحباً بك مجدداً" card (:24308-24331), the second-factor step and the first-login enrolment step (D-036),
// and the preview banner slot (D-223). See tests/visual/DEVIATIONS.md DEV-01, DEV-02.
import { useState } from 'react';
import { motion } from 'motion/react';
import { ArrowLeft, ArrowUpRight, KeyRound, ShieldCheck, User, X } from 'lucide-react';
import { Logo } from '../components/HelperComponents.js';
import { PreviewBanner, type PreviewBannerData } from '../components/PreviewBanner.js';
import { TotpQr, TotpSecretText } from '../components/TotpEnrolment.js';
import { useI18n } from '../i18n-context.js';

export type LoginStep = 'credentials' | 'second_factor' | 'enrol_second_factor';

export interface LoginProps {
  readonly step: LoginStep;
  readonly username: string;
  readonly password: string;
  /** TOTP code (second factor / enrolment confirmation). */
  readonly code: string;
  readonly onUsernameChange: (value: string) => void;
  readonly onPasswordChange: (value: string) => void;
  readonly onCodeChange: (value: string) => void;
  readonly onSubmit: () => void;
  /** "تغيير الحساب": back to the credentials step. */
  readonly onBack?: () => void;
  readonly onForgotPassword?: () => void;
  /** Enrolment data from POST /auth/totp/enrol (secret + otpauth URI as text, no QR library). */
  readonly enrolment?: { readonly secret: string; readonly uri: string } | null;
  /** Recovery codes returned by POST /auth/totp/confirm; shown once with a continue button. */
  readonly recoveryCodes?: readonly string[] | null;
  readonly onContinue?: () => void;
  readonly error?: string | null;
  readonly busy?: boolean;
  readonly banner?: PreviewBannerData | null;
  readonly logoUrl?: string | null;
}

// App.tsx:24346 — the password input classes, reused for every credential field (D-038).
const INPUT_CLASS =
  'w-full bg-slate-50 dark:bg-[#05070a] border-2 border-slate-100 dark:border-[#30363d] rounded-[2rem] px-16 py-6 focus:outline-none focus:ring-8 focus:ring-primary-500/10 focus:border-primary-500 transition-all dark:text-[#f0f6fc] text-center text-3xl tracking-[0.6em] font-black shadow-inner';
const LABEL_CLASS = 'block text-sm font-black text-gray-600 dark:text-[#8b949e] mb-4 uppercase tracking-[0.2em]';

export function Login(props: LoginProps) {
  const { t, locale } = useI18n();
  const lang = locale;
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const { step, error, busy } = props;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!busy) props.onSubmit();
  };

  const errorBox = error ? (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="login-error"
      className="flex items-center justify-center gap-2 text-rose-600 dark:text-rose-400 text-sm font-black bg-rose-50 dark:bg-rose-900/20 py-4 rounded-2xl border border-rose-100 dark:border-rose-800/30"
    >
      <X size={18} />
      {error}
    </motion.div>
  ) : null;

  const submitButton = (label: string) => (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      type="submit"
      disabled={busy}
      data-testid="login-submit"
      className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white py-6 rounded-2xl font-black text-2xl hover:from-primary-500 hover:to-primary-600 transition-all shadow-2xl shadow-primary-600/40 flex items-center justify-center gap-4 group relative overflow-hidden"
    >
      <div className="absolute inset-0 animate-shimmer opacity-20" />
      <span className="relative z-10">{label}</span>
      <ArrowUpRight
        className="relative z-10 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
        size={28}
      />
    </motion.button>
  );

  return (
    <div
      className={`flex min-h-screen bg-[var(--bg-main)] items-center justify-center font-sans p-6 ${lang === "ar" ? "font-arabic" : ""}`}
      dir={lang === "ar" ? "rtl" : "ltr"}
    >
      {props.banner && (
        <div className="fixed top-0 inset-x-0 z-50">
          <PreviewBanner banner={props.banner} />
        </div>
      )}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="premium-card p-12 md:p-16 w-full max-w-2xl relative overflow-hidden group"
      >
        {/* Decorative background element */}
        <div className="absolute -top-32 -right-32 w-64 h-64 bg-primary-500/10 rounded-full blur-3xl transition-transform group-hover:scale-110 duration-1000" />
        <div className="absolute -bottom-32 -left-32 w-64 h-64 bg-rose-500/10 rounded-full blur-3xl transition-transform group-hover:scale-110 duration-1000" />
        <div className="text-center mb-16 relative z-10">
          <div className="inline-block p-6 bg-primary-50 dark:bg-primary-900/20 rounded-[2.5rem] mb-8 shadow-inner border border-primary-100 dark:border-primary-500/10">
            <Logo size={80} className="mx-auto" {...(props.logoUrl !== undefined ? { logoUrl: props.logoUrl } : {})} />
          </div>
          <h1 className="text-5xl font-black text-gray-900 dark:text-[#f0f6fc] tracking-tighter mb-4 dark:digital-glow">
            {t('title')}
          </h1>
          <p className="text-gray-500 dark:text-[#8b949e] font-black text-xl uppercase tracking-[0.2em]">
            {t('login.subtitle')}
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8 relative z-10"
        >
          {/* App.tsx:24308-24331 — the welcome card; the username is typed here (D-038) */}
          <div className="flex items-center gap-6 p-8 bg-primary-50/50 dark:bg-primary-900/10 rounded-[2.5rem] border border-primary-100 dark:border-primary-800/30 backdrop-blur-md shadow-inner">
            <div className="w-20 h-20 rounded-[1.5rem] bg-primary-600 flex items-center justify-center text-white shadow-2xl ring-8 ring-primary-500/10 group-hover:rotate-3 transition-transform">
              {step === 'credentials' ? <User size={40} /> : <KeyRound size={40} />}
            </div>
            <div className="flex-1">
              <p className="text-sm font-black text-primary-600 dark:text-primary-400 mb-1 uppercase tracking-[0.2em]">
                {step === 'credentials' ? t('login.welcome_back') : step === 'second_factor' ? t('login.second_factor_title') : t('login.enrol_title')}
              </p>
              {step === 'credentials' ? (
                <form onSubmit={submit}>
                  <label className="sr-only" htmlFor="login-username">{t('login.username')}</label>
                  <input
                    id="login-username"
                    data-testid="login-username"
                    type="text"
                    required
                    autoFocus
                    autoComplete="username"
                    autoCapitalize="off"
                    spellCheck={false}
                    value={props.username}
                    onChange={(e) => props.onUsernameChange(e.target.value)}
                    placeholder={t('login.username')}
                    className="w-full bg-transparent text-3xl font-black text-gray-900 dark:text-[#f0f6fc] uppercase tracking-tighter dark:digital-glow focus:outline-none placeholder:text-gray-300 dark:placeholder:text-[#30363d] placeholder:normal-case placeholder:tracking-normal placeholder:text-xl"
                  />
                  {/* App.tsx:24322-24331 — the "تغيير الحساب" line (text-sm, mt-2) is removed by D-038; an empty line of
                      the same box keeps the welcome card at its old height so nothing below it moves (DEVIATIONS.md V-01). */}
                  <div aria-hidden="true" data-testid="login-change-account-slot" className="text-sm mt-2 h-5" />
                </form>
              ) : (
                <>
                  <h3 className="text-3xl font-black text-gray-900 dark:text-[#f0f6fc] uppercase tracking-tighter dark:digital-glow">
                    {props.username}
                  </h3>
                  {props.onBack && (
                    <button
                      type="button"
                      onClick={props.onBack}
                      data-testid="login-back"
                      className="text-sm text-primary-600 dark:text-primary-400 font-black hover:underline mt-2 flex items-center gap-2 uppercase tracking-widest group/btn"
                    >
                      <ArrowLeft size={14} className="group-hover/btn:-translate-x-1 transition-transform" />
                      {t('login.change_account')}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {step === 'credentials' && (
            <form onSubmit={submit} className="space-y-8">
              <div>
                <label className={LABEL_CLASS}>{t('login.password')}</label>
                <div className="relative">
                  <ShieldCheck
                    className="absolute right-6 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-[#30363d]"
                    size={28}
                  />
                  <input
                    type={showLoginPassword ? "text" : "password"}
                    required
                    autoComplete="current-password"
                    data-testid="login-password"
                    value={props.password}
                    onChange={(e) => props.onPasswordChange(e.target.value)}
                    className={INPUT_CLASS}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowLoginPassword(!showLoginPassword)}
                    className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary-600 transition-colors"
                  >
                    {showLoginPassword ? (
                      <X size={28} />
                    ) : (
                      <div className="w-8 h-8 border-2 border-gray-400 rounded-full hover:border-primary-600 transition-colors" />
                    )}
                  </button>
                </div>
              </div>
              {errorBox}
              {submitButton(t('login.enter_system'))}
            </form>
          )}

          {step === 'second_factor' && (
            <form onSubmit={submit} className="space-y-8">
              <div>
                <label className={LABEL_CLASS}>{t('login.second_factor_code')}</label>
                <div className="relative">
                  <KeyRound
                    className="absolute right-6 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-[#30363d]"
                    size={28}
                  />
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    required
                    autoFocus
                    autoComplete="one-time-code"
                    data-testid="login-code"
                    value={props.code}
                    onChange={(e) => props.onCodeChange(e.target.value)}
                    className={`${INPUT_CLASS} font-mono`}
                    placeholder="••••••"
                  />
                </div>
                <p className="text-xs font-bold text-gray-400 dark:text-[#8b949e] mt-3 text-center">{t('login.second_factor_hint')}</p>
              </div>
              {errorBox}
              {submitButton(t('login.verify'))}
            </form>
          )}

          {step === 'enrol_second_factor' && (
            props.recoveryCodes && props.recoveryCodes.length > 0 ? (
              <div className="space-y-8">
                <div>
                  <label className={LABEL_CLASS}>{t('login.recovery_codes_title')}</label>
                  <div data-testid="login-recovery-codes" className="grid grid-cols-2 gap-2 p-6 bg-slate-50 dark:bg-[#05070a] border-2 border-slate-100 dark:border-[#30363d] rounded-[2rem] font-mono text-lg font-black text-gray-900 dark:text-[#f0f6fc] text-center" dir="ltr">
                    {props.recoveryCodes.map((c) => (
                      <span key={c}>{c}</span>
                    ))}
                  </div>
                  <p className="text-xs font-bold text-gray-400 dark:text-[#8b949e] mt-3 text-center">{t('login.recovery_codes_hint')}</p>
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  onClick={props.onContinue}
                  data-testid="login-continue"
                  className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white py-6 rounded-2xl font-black text-2xl hover:from-primary-500 hover:to-primary-600 transition-all shadow-2xl shadow-primary-600/40 flex items-center justify-center gap-4 group relative overflow-hidden"
                >
                  <div className="absolute inset-0 animate-shimmer opacity-20" />
                  <span className="relative z-10">{t('login.continue')}</span>
                  <ArrowUpRight className="relative z-10 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" size={28} />
                </motion.button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-8">
                <div className="space-y-4">
                  <p className="text-xs font-bold text-gray-400 dark:text-[#8b949e] text-center">{t('login.enrol_qr')}</p>
                  {props.enrolment?.uri ? <TotpQr uri={props.enrolment.uri} label={t('login.enrol_qr')} testId="login-enrol-qr" /> : null}
                  <div>
                    <label className={LABEL_CLASS}>{t('login.enrol_manual')} — {t('login.enrol_secret')}</label>
                    <TotpSecretText
                      secret={props.enrolment?.secret ?? ''}
                      testId="login-enrol-secret"
                      className="w-full bg-slate-50 dark:bg-[#05070a] border-2 border-slate-100 dark:border-[#30363d] rounded-[2rem] px-6 py-5 text-center font-mono text-xl font-black tracking-[0.15em] text-gray-900 dark:text-[#f0f6fc] shadow-inner select-all"
                    />
                    <p className="text-[11px] font-bold text-gray-400 dark:text-[#8b949e] mt-2 text-center">{t('login.enrol_alphabet')}</p>
                  </div>
                </div>
                <div>
                  <label className={LABEL_CLASS}>{t('login.second_factor_code')}</label>
                  <div className="relative">
                    <KeyRound
                      className="absolute right-6 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-[#30363d]"
                      size={28}
                    />
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]{6}"
                      maxLength={6}
                      required
                      autoComplete="one-time-code"
                      data-testid="login-code"
                      value={props.code}
                      onChange={(e) => props.onCodeChange(e.target.value)}
                      className={`${INPUT_CLASS} font-mono`}
                      placeholder="••••••"
                    />
                  </div>
                </div>
                {errorBox}
                {submitButton(t('login.enrol_confirm'))}
              </form>
            )
          )}
        </motion.div>

        {/* App.tsx:24392-24458 — footer; the WhatsApp recovery link becomes a plain "ask your manager" line (D-037) */}
        <div className="pt-10 mt-10 border-t border-gray-100 dark:border-gray-800 text-center relative z-10 space-y-4">
          <button
            type="button"
            onClick={props.onForgotPassword}
            className="inline-flex items-center gap-2 text-gray-400 dark:text-gray-500 text-sm hover:text-green-600 dark:hover:text-green-400 transition-all font-bold"
          >
            <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center group-hover:bg-green-100 transition-colors">
              <ShieldCheck size={16} />
            </div>
            {t('login.forgot_password')}
          </button>
          {/* Shafaq Trade Group Ownership & Support */}
          <div className="pt-4 border-t border-dashed border-gray-100 dark:border-gray-800/40 flex flex-col items-center gap-1">
            <span className="text-[10px] text-gray-400 dark:text-gray-500 font-bold uppercase tracking-tight">
              {t('login.ownership')}
            </span>
            <span className="text-[11px] text-amber-500 dark:text-amber-400 font-black">
              {t('login.support_name')}
            </span>
            <span className="flex items-center gap-1 mt-1 text-[11px] font-black text-emerald-600 dark:text-emerald-400">
              <span>
                📞{" "}
                {t('login.support_contact')}
              </span>
            </span>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
